<template>
   <div v-bind:class="[{ busy: loadingMedia}]" class="media-gallery opacity-transition">
      <pb-spinner v-if="loadingMedia"></pb-spinner>

      <!-- Filter tabs for media types -->
      <b-card v-if="!imageOnly" no-body class="mb-3">
         <b-card-header header-tag="nav">
            <b-nav card-header tabs>
               <b-nav-item
                   :active="selectedType === null"
                   @click="filterByType(null)">
                  All
               </b-nav-item>
               <b-nav-item
                   :active="selectedType === 'IMAGE'"
                   @click="filterByType('IMAGE')">
                  Images
               </b-nav-item>
               <b-nav-item
                   :active="selectedType === 'VIDEO'"
                   @click="filterByType('VIDEO')">
                  Videos
               </b-nav-item>
               <b-nav-item
                   :active="selectedType === 'DOCUMENT'"
                   @click="filterByType('DOCUMENT')">
                  Documents
               </b-nav-item>
            </b-nav>
         </b-card-header>
         <b-card-body v-if="mediaList.length > 0 || searchTerm">
            <b-row>
               <b-col>
                  <!-- Search and Upload button -->
                  <b-row class="mb-3">
                     <b-col>
                        <b-pagination
                            v-model="currentPage"
                            :total-rows="totalItems"
                            :per-page="pageSize"
                            aria-controls="mediaList"
                        ></b-pagination>
                     </b-col>
                     <b-col>
                        <b-form @submit.prevent>
                           <b-input-group>
                              <b-form-input
                                  v-model="searchTerm"
                                  v-debounce:500="handleSearch"
                                  placeholder="Search files..."
                                  type="text">
                              </b-form-input>
                              <b-input-group-append>
                                 <b-input-group-text>
                                    <i class="fal fa-search"></i>
                                 </b-input-group-text>
                              </b-input-group-append>
                           </b-input-group>
                        </b-form>
                     </b-col>
                     <b-col md="auto">
                        <b-button variant="primary" @click="openCreateMediaModal">
                           <i class="fal fa-upload"></i> Upload Media
                        </b-button>
                     </b-col>
                  </b-row>

                  <!-- Media grid -->
                  <b-row v-bind:class="[{ busy: uploadingMedia}, 'opacity-transition']">
                     <pb-spinner v-if="uploadingMedia"></pb-spinner>
                     <b-col v-bind:key="media.id" md="3" class="my-3" v-for="media in mediaList">
                        <div class="media-item-wrapper">
                           <media-card
                               :media="media"
                               @click="previewMedia(media)">
                           </media-card>

                           <!-- Action buttons -->
                           <div class="media-actions" v-if="enableActions">
                              <b-button-group size="sm" class="w-100">
                                 <b-button variant="outline-info" @click.stop="generateReference(media)" v-b-tooltip.hover
                                           title="Generate ERP Reference">
                                    <i class="fal fa-file-code"></i>
                                 </b-button>
                                 <b-button variant="outline-danger" @click.stop="confirmDelete(media)" v-b-tooltip.hover
                                           title="Delete">
                                    <i class="fal fa-trash"></i>
                                 </b-button>
                              </b-button-group>
                           </div>
                        </div>
                     </b-col>
                  </b-row>

                  <!-- Bottom pagination -->
                  <b-row>
                     <b-col>
                        <b-pagination
                            v-model="currentPage"
                            :total-rows="totalItems"
                            :per-page="pageSize"
                            aria-controls="mediaList"
                        ></b-pagination>
                     </b-col>
                  </b-row>
               </b-col>
            </b-row>
         </b-card-body>

         <!-- Empty state -->
         <b-card-body v-else>
            <b-row>
               <b-col class="text-center py-5">
                  <i class="fal fa-folder-open fa-3x text-muted mb-3"></i>
                  <p class="text-muted">No media files found.</p>
                  <b-button variant="primary" @click="openCreateMediaModal" class="mt-2">
                     <i class="fal fa-upload"></i> Upload Media
                  </b-button>
               </b-col>
            </b-row>
         </b-card-body>
      </b-card>


      <!-- Edit/Create Media Modal -->
      <b-modal v-model="showEditModal"
               :title="isCreatingNew ? 'Upload New Media' : 'Media Details & Edit'"
               @ok="saveMediaEdit"
               size="xl">
         <b-form v-if="editingMedia">
            <!-- Two-column layout for existing media -->
            <b-row v-if="!isCreatingNew">
               <!-- Left Column: Preview & File Info -->
               <b-col md="5">
                  <!-- Show new file preview if selected, otherwise show current media -->
                  <div class="media-detail-preview mb-3">
                     <div v-if="selectedFile && selectedFilePreviewUrl" class="preview-stage">
                        <div class="new-file-badge">New File Preview</div>
                        <img v-if="isImageFile(selectedFile)" :src="selectedFilePreviewUrl" alt="New file preview" />
                        <video v-else-if="isVideoFile(selectedFile)" controls>
                           <source :src="selectedFilePreviewUrl" :type="selectedFile.type">
                        </video>
                        <div v-else class="preview-stage document-stage">
                           <div class="download-center">
                              <i class="fal fa-file fa-4x text-muted mb-3"></i>
                              <p class="text-muted">{{ selectedFile.name }}</p>
                           </div>
                        </div>
                     </div>
                     <media-preview v-else :media="editingMedia" :show-info="false"></media-preview>
                  </div>

                  <!-- Replace File Button -->
                  <div class="mb-3">
                     <b-button variant="outline-primary" @click="triggerReplaceFileInput" block>
                        <i class="fal fa-sync-alt"></i> Replace File
                     </b-button>
                     <span v-if="selectedFile" class="text-success d-block mt-2 text-center">
                        <small><i class="fal fa-check"></i> {{ selectedFile.name }}</small>
                     </span>
                     <input
                         type="file"
                         ref="replaceFileInput"
                         @change="handleModalFileSelect"
                         style="display: none"
                         accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx">
                  </div>

                  <div class="file-info-section">
                     <h6 class="mb-3">File Information</h6>
                     <table class="table table-sm">
                        <tbody>
                           <tr>
                              <td class="text-muted" width="40%">Original Filename:</td>
                              <td><small>{{ editingMedia.originalFilename }}</small></td>
                           </tr>
                           <tr>
                              <td class="text-muted">System Filename:</td>
                              <td><small>{{ editingMedia.filename }}</small></td>
                           </tr>
                           <tr>
                              <td class="text-muted">Type:</td>
                              <td><span class="badge badge-secondary">{{ editingMedia.mediaType }}</span></td>
                           </tr>
                           <tr>
                              <td class="text-muted">Size:</td>
                              <td>{{ formatFileSize(editingMedia.fileSize) }}</td>
                           </tr>
                           <tr v-if="editingMedia.createdAt">
                              <td class="text-muted">Created:</td>
                              <td>{{ formatDate(editingMedia.createdAt) }}</td>
                           </tr>
                        </tbody>
                     </table>
                  </div>
               </b-col>

               <!-- Right Column: Edit Form -->
               <b-col md="7">
                  <!-- Title (human-readable identifier) -->
                  <b-form-group label="Title">
                     <b-form-input
                         v-model="editingMedia.title"
                         placeholder="Title">
                     </b-form-input>
                     <small class="form-text text-muted">Human-readable name for this media file</small>
                  </b-form-group>

                  <b-form-group label="Description">
                     <b-form-textarea v-model="editingMedia.description" rows="4"></b-form-textarea>
                  </b-form-group>

                  <b-form-group label="Tags (comma-separated)">
                     <b-form-input v-model="editingMedia.tags" placeholder="e.g., product, diamond, 2024"></b-form-input>
                  </b-form-group>
               </b-col>
            </b-row>

            <!-- Single column for creating new media -->
            <div v-else>
               <!-- File Upload -->
               <b-form-group label="Select File">
                  <div class="d-flex align-items-center">
                     <b-button variant="outline-primary" @click="triggerCreateFileInput" class="mr-3">
                        <i class="fal fa-upload"></i> Choose File
                     </b-button>
                     <span v-if="selectedFile" class="text-muted">{{ selectedFile.name }}</span>
                     <span v-else class="text-muted">No file selected</span>
                  </div>
                  <input
                      type="file"
                      ref="createFileInput"
                      @change="handleModalFileSelect"
                      style="display: none"
                      accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx">
               </b-form-group>

               <!-- Title -->
               <b-form-group label="Title">
                  <b-form-input
                      v-model="editingMedia.title"
                      placeholder="title">
                  </b-form-input>
                  <small class="form-text text-muted">Human-readable name for this media file</small>
               </b-form-group>

               <b-form-group label="Description">
                  <b-form-textarea v-model="editingMedia.description" rows="3"></b-form-textarea>
               </b-form-group>

               <b-form-group label="Tags (comma-separated)">
                  <b-form-input v-model="editingMedia.tags" placeholder="e.g., product, diamond, 2024"></b-form-input>
               </b-form-group>
            </div>
         </b-form>
      </b-modal>
   </div>
</template>

<script>
import FileApi from "../../../shared/_api/file";
import MediaCard from './MediaCard.vue';
import MediaPreview from './MediaPreview.vue';

export default {
   name: 'MediaGallery',
   components: {
      MediaCard,
      MediaPreview
   },
   props: {
      selectImageCallback: Function,  // Legacy prop for backward compatibility
      selectMediaCallback: Function,  // New prop for media selection
      imageOnly: {                    // Set to true to only show images (legacy mode)
         type: Boolean,
         default: false
      },
      enableActions: {                // Enable edit/delete actions
         type: Boolean,
         default: true
      }
   },
   data() {
      return {
         currentPage: 1,
         pageSize: 12,
         totalItems: 0,
         mediaList: [],
         loadingMedia: false,
         searchTerm: '',
         selectedType: null,
         uploadingMedia: false,
         showEditModal: false,
         editingMedia: null,
         isCreatingNew: false,
         selectedFile: null,
         selectedFilePreviewUrl: null
      };
   },
   created() {
      this.loadMedia();
   },
   watch: {
      currentPage() {
         this.loadMedia();
      }
   },
   methods: {
      loadMedia() {
         this.loadingMedia = true;

         const params = {
            page: this.currentPage - 1,
            size: this.pageSize,
            sortBy: 'createdAt',
            sortDir: 'desc'
         };

         if (this.selectedType) {
            params.type = this.selectedType;
         }

         if (this.searchTerm) {
            params.search = this.searchTerm;
         }

         // ALWAYS use new media API, but filter to IMAGE type if imageOnly
         if (this.imageOnly) {
            params.type = 'IMAGE';
         }

         FileApi.fetchMedia(params).then(response => {
            this.mediaList = response.content || response;
            this.totalItems = response.totalElements || this.mediaList.length;
         }).finally(() => {
            this.loadingMedia = false;
         });
      },

      filterByType(type) {
         this.selectedType = type;
         this.currentPage = 1;
         this.searchTerm = ''; // Reset search when switching tabs
         this.loadMedia();
      },

      handleSearch() {
         this.currentPage = 1;
         this.loadMedia();
      },

      previewMedia(media) {
         // Open edit modal instead of preview modal
         this.editMedia(media);
      },

      selectMedia(media) {
         // Support both legacy and new callbacks
         if (this.selectImageCallback) {
            this.selectImageCallback(media.filename);
         }
         if (this.selectMediaCallback) {
            this.selectMediaCallback(media);
         }
      },

      openCreateMediaModal() {
         this.isCreatingNew = true;
         this.selectedFile = null;
         this.editingMedia = {
            title: '',
            description: '',
            tags: ''
         };
         this.showEditModal = true;
      },

      editMedia(media) {
         this.isCreatingNew = false;
         this.selectedFile = null;
         this.clearFilePreview();
         this.editingMedia = {...media};
         this.showEditModal = true;
      },

      triggerReplaceFileInput() {
         this.$refs.replaceFileInput.click();
      },

      triggerCreateFileInput() {
         this.$refs.createFileInput.click();
      },

      handleModalFileSelect(event) {
         const file = event.target.files[0];
         if (file) {
            this.selectedFile = file;
            // Create preview URL for images and videos
            this.clearFilePreview();
            if (this.isImageFile(file) || this.isVideoFile(file)) {
               this.selectedFilePreviewUrl = URL.createObjectURL(file);
            }
         }
         // Reset input so same file can be selected again
         event.target.value = '';
      },

      isImageFile(file) {
         return file && file.type && file.type.startsWith('image/');
      },

      isVideoFile(file) {
         return file && file.type && file.type.startsWith('video/');
      },

      clearFilePreview() {
         if (this.selectedFilePreviewUrl) {
            URL.revokeObjectURL(this.selectedFilePreviewUrl);
            this.selectedFilePreviewUrl = null;
         }
      },

      saveMediaEdit() {
         if (!this.editingMedia) return;

         this.uploadingMedia = true;

         if (this.isCreatingNew) {
            // Creating new media - file is required
            if (!this.selectedFile) {
               this.$bvToast.toast('Please select a file to upload', {
                  title: 'Error',
                  variant: 'warning',
                  autoHideDelay: 3000
               });
               this.uploadingMedia = false;
               return;
            }

            FileApi.uploadMedia(this.selectedFile).then((media) => {
               // Update metadata if provided
               if (this.editingMedia.title || this.editingMedia.description || this.editingMedia.tags) {
                  const updateData = {
                     title: this.editingMedia.title,
                     description: this.editingMedia.description,
                     tags: this.editingMedia.tags
                  };
                  return FileApi.updateMedia(media.id, updateData);
               }
               return media;
            }).then(() => {
               this.$bvToast.toast('Media uploaded successfully', {
                  title: 'Success',
                  variant: 'success',
                  autoHideDelay: 3000
               });
               this.showEditModal = false;
               this.loadMedia();
            }).catch((error) => {
               console.error('Upload error:', error);
               this.$bvToast.toast('Failed to upload media', {
                  title: 'Error',
                  variant: 'danger',
                  autoHideDelay: 5000
               });
            }).finally(() => {
               this.uploadingMedia = false;
            });

         } else {
            // Editing existing media
            const operations = [];

            // If file is selected, replace it first
            if (this.selectedFile) {
               operations.push(
                  FileApi.replaceMedia(this.editingMedia.id, this.selectedFile)
               );
            }

            // Update metadata
            const updateData = {
               title: this.editingMedia.title,
               description: this.editingMedia.description,
               tags: this.editingMedia.tags
            };
            operations.push(
               FileApi.updateMedia(this.editingMedia.id, updateData)
            );

            Promise.all(operations).then(() => {
               const message = this.selectedFile
                  ? 'Media file replaced and updated successfully'
                  : 'Media updated successfully';
               this.$bvToast.toast(message, {
                  title: 'Success',
                  variant: 'success',
                  autoHideDelay: 3000
               });
               this.clearFilePreview();
               this.showEditModal = false;
               this.loadMedia();
            }).catch((error) => {
               console.error('Update error:', error);
               this.$bvToast.toast('Failed to update media', {
                  title: 'Error',
                  variant: 'danger',
                  autoHideDelay: 5000
               });
            }).finally(() => {
               this.uploadingMedia = false;
            });
         }
      },

      // Cleanup when modal closes
      beforeDestroy() {
         this.clearFilePreview();
      },

      confirmDelete(media) {
         this.$bvModal.msgBoxConfirm(`Are you sure you want to delete "${media.originalFilename}"?`, {
            title: 'Confirm Delete',
            okVariant: 'danger',
            okTitle: 'Delete',
            cancelTitle: 'Cancel'
         }).then(confirmed => {
            if (confirmed) {
               this.deleteMedia(media);
            }
         });
      },

      generateReference(media) {
         FileApi.generateReference(media.id, media).then((result) => {
            this.$bvToast.toast(`JSON reference file "${result.filename}" downloaded. Upload this to your ERP article files folder.`, {
               title: 'Reference Generated',
               variant: 'success',
               autoHideDelay: 5000
            });
         }).catch((error) => {
            console.error('Generate reference error:', error);
            this.$bvToast.toast('Failed to generate reference file', {
               title: 'Error',
               variant: 'danger',
               autoHideDelay: 5000
            });
         });
      },

      formatFileSize(bytes) {
         if (!bytes) return '';
         if (bytes < 1024) return bytes + ' B';
         if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
         return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
      },

      formatDate(dateString) {
         if (!dateString) return '';
         const date = new Date(dateString);
         return date.toLocaleDateString();
      },

      deleteMedia(media) {
         FileApi.deleteMedia(media.id).then(() => {
            this.$bvToast.toast('Media deleted successfully', {
               title: 'Success',
               variant: 'success',
               autoHideDelay: 3000
            });
            this.loadMedia();
         }).catch(() => {
            this.$bvToast.toast('Failed to delete media', {
               title: 'Error',
               variant: 'danger',
               autoHideDelay: 5000
            });
         });
      }
   }
}
</script>

<style lang="scss" scoped>
.media-gallery {
   min-height: 300px;
}

.media-detail-preview {
   border: 1px solid #dee2e6;
   border-radius: 8px;
   overflow: hidden;
   position: relative;

   .new-file-badge {
      position: absolute;
      top: 10px;
      right: 10px;
      background: #28a745;
      color: white;
      padding: 4px 12px;
      border-radius: 4px;
      font-size: 0.75rem;
      font-weight: 600;
      z-index: 10;
   }

   .preview-stage {
      width: 100%;
      height: 400px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f8f9fa;

      img, video {
         max-width: 100%;
         max-height: 100%;
         width: auto;
         height: auto;
         object-fit: contain;
      }

      &.document-stage {
         .download-center {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
         }
      }
   }
}

.file-info-section {
   h6 {
      font-weight: 600;
      color: #495057;
   }

   .table {
      margin-bottom: 0;

      td {
         padding: 0.5rem;
         border-top: 1px solid #e9ecef;

         &:first-child {
            font-size: 0.875rem;
         }
      }

      tr:first-child td {
         border-top: none;
      }
   }
}

.media-item-wrapper {
   position: relative;

   .media-type-badge {
      position: absolute;
      top: 8px;
      right: 8px;
      background: rgba(0, 0, 0, 0.7);
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 0.75rem;
      z-index: 10;
   }

   .media-actions {
      margin-top: 8px;
      text-align: center;
   }
}
</style>
